import { decode as atob, encode as btoa } from 'base-64';
import moment from 'moment';
import { Capacitor } from '@capacitor/core';

import storage from './storage';

const ACCESS_TOKEN = 'ownly-at';
const AUTH_DATA = 'ownly-ad';
const CURRENT_COMPANY_ID = 'ownly-cId';
const ROLE = 'ownly-r';
const DEVICE_TOKEN = 'ownly-dt';
const SIDEBAR_EXPANDED = 'ownly-se';
const AUTH_REDIRECT_URI = 'ownly-a-uri';
const AUTH_STATE = 'ownly-a-state';
const LOGIN_EMAIL = 'ownly-le';
const LOGIN_REDIRECT_PATH = 'ownly-rp';

const cache = new Map();

const isNative = Capacitor.isNativePlatform();

async function init() {
  if (!isNative) return; // Use cookies on the web and cache on native platforms.

  console.log('[Storage] Initializing cache...');

  const keys = [
    ACCESS_TOKEN,
    AUTH_DATA,
    CURRENT_COMPANY_ID,
    ROLE,
    DEVICE_TOKEN,
    AUTH_REDIRECT_URI,
    AUTH_STATE,
    LOGIN_EMAIL,
  ];

  await Promise.all(keys.map(async (key) => {
    let value = await storage.get(key);
    if (value && typeof value === 'object') value = JSON.stringify(value);
    cache.set(key, value);
  }));
}

// Reads from the in-memory cache on native, or directly from cookies on web
function readValue(key) {
  return isNative ? cache.get(key) : storage.getSync(key);
}

// Writes to the in-memory cache (native only) and always persists to storage
function writeValue(key, value, options = {}) {
  if (isNative) cache.set(key, value);
  storage.set(key, value, options).catch(() => {});
}

function setCurrentCompanyId(currentCompanyId) {
  writeValue(CURRENT_COMPANY_ID, currentCompanyId);
}

function getCurrentCompanyId() {
  return parseInt(readValue(CURRENT_COMPANY_ID), 10);
}

function setAccessToken(accessToken) {
  writeValue(ACCESS_TOKEN, accessToken);
}

function getAccessToken() {
  return readValue(ACCESS_TOKEN);
}

function setDeviceToken(deviceToken) {
  const expiryDate = moment().add(10, 'years').toDate();
  writeValue(DEVICE_TOKEN, deviceToken, { expires: expiryDate });
}

function getDeviceToken() {
  return readValue(DEVICE_TOKEN);
}

function setRole(role) {
  writeValue(ROLE, btoa(role));
}

function getRole() {
  try {
    return atob(readValue(ROLE));
  } catch {
    return null;
  }
}

function setAuthData(authData) {
  writeValue(ACCESS_TOKEN, authData.accessToken);
  writeValue(AUTH_DATA, JSON.stringify(authData));
}

function getAuthData() {
  const value = readValue(AUTH_DATA);
  try {
    return (typeof value === 'string') ? JSON.parse(value) : value;
  } catch {
    return null;
  }
}

function setSidebarExpanded(isExpanded) {
  sessionStorage.setItem(SIDEBAR_EXPANDED, isExpanded);
}

function isSidebarExpanded() {
  return sessionStorage.getItem(SIDEBAR_EXPANDED) === 'true';
}

function resetSession() {
  if (isNative) {
    cache.delete(ACCESS_TOKEN);
    cache.delete(AUTH_DATA);
    cache.delete(CURRENT_COMPANY_ID);
    cache.delete(ROLE);
  }
  Promise.all([
    storage.remove(ACCESS_TOKEN),
    storage.remove(AUTH_DATA),
    storage.remove(CURRENT_COMPANY_ID),
    storage.remove(ROLE)
  ]).catch(() => {});
  sessionStorage.removeItem(SIDEBAR_EXPANDED);
}

function setAuthRedirectURI(redirectURI) {
  writeValue(AUTH_REDIRECT_URI, redirectURI);
}

function getAuthRedirectUri() {
  return readValue(AUTH_REDIRECT_URI);
}

function setAuthState(state) {
  writeValue(AUTH_STATE, state);
}

function getAuthState() {
  return readValue(AUTH_STATE);
}

function setLoginEmail(loginEmail) {
  writeValue(LOGIN_EMAIL, loginEmail);
}

function getLoginEmail() {
  return readValue(LOGIN_EMAIL);
}

function setLoginRedirectPath(redirectPath) {
  writeValue(LOGIN_REDIRECT_PATH, redirectPath);
}

function getLoginRedirectPath() {
  return readValue(LOGIN_REDIRECT_PATH);
}

const SecureStore = {
  init,
  setCurrentCompanyId,
  getCurrentCompanyId,
  resetSession,
  setAccessToken,
  getAccessToken,
  setRole,
  getRole,
  setAuthData,
  getAuthData,
  setDeviceToken,
  getDeviceToken,
  setSidebarExpanded,
  isSidebarExpanded,
  setAuthRedirectURI,
  getAuthRedirectUri,
  setAuthState,
  getAuthState,
  setLoginEmail,
  getLoginEmail,
  setLoginRedirectPath,
  getLoginRedirectPath,
};

export default SecureStore;
